/**
 * Lightweight document monitoring (frontend detection layer)
 *
 * Design goals:
 * - MediaPipe runs continuously in-browser (low latency, scalable).
 * - Only objective signals are tracked locally.
 * - Only when entering SUSPICIOUS/HIGH_RISK do we send a small payload:
 *   - max 1–2 cropped images (face + nearby region)
 *   - MediaPipe metadata + simple motion/occlusion stats
 *
 * Non-goals:
 * - No continuous webcam uploads
 * - No frame-by-frame AI analysis
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/config/api';
import { faceDetectionService } from '@/utils/faceDetection';

const MonitorState = Object.freeze({
  IDLE: 'IDLE',
  SUSPICIOUS: 'SUSPICIOUS',
  HIGH_RISK: 'HIGH_RISK',
});

// ===== CRITICAL FIXES =====
// BUG FIX 1: Removed periodic phone detection intervals (was firing every 800ms)
// BUG FIX 2: Increased motion thresholds to realistic levels (was 1/1.5, now 20/25)
// BUG FIX 3: Increased face size change threshold to 0.18 (was 0.08 - breathing triggers it)
// BUG FIX 4: Proper initialization of lastFaceSeenAtRef to null (was 0)
// BUG FIX 16: Ensure frozen diff detection only fires after first frame exists

// These defaults are tuned to reduce false positives while maintaining security
const THRESHOLDS = Object.freeze({
  // Face count policy:
  // - 0/1 face is normal (other rules still apply)
  // - 2–3 faces: do NOT lock (user may show document to someone, normal behavior)
  // - 4+ faces: HIGH_RISK -> send event + backend enforces
  maxAllowedFaces: 3,
  lockFaces: 4,
  
  // Face disappearance (ms)
  faceGoneMs: 3000, // 3 second tolerance for MediaPipe temporary failures
  
  // Face position jump threshold (normalized distance 0..~1)
  // Leaning 1-2 inches = ~5% movement; breathing = ~2% movement
  faceJumpDist: 0.35, // Increased from 0.30 to allow normal movements
  
  // Dark / blocked camera thresholds
  darkLuma: 8, // Very dark threshold (prevent false positives from shadows)
  darkHoldMs: 4000, // 4 seconds of darkness required before triggering
  darkConsecutiveFrames: 20, // 20 consecutive dark frames for stability
  cameraMaturityMs: 6000, // 6 second grace period after camera starts (prevents startup noise)
  
  // Frozen camera thresholds
  frozenDiff: 0.5, // FIXED: Lower from 1.0 to reduce sensitivity to codec artifacts
  frozenHoldMs: 3000, // Increased from 1500ms for stability
  
  // Motion thresholds - REALISTIC values (not 1/1.5)
  // Normal head movement: 5-15 diff per frame
  // Normal breathing: 1-5 diff per frame
  // Codec artifacts: can produce 1-3 diff on still frames
  // Phone held to screen: 25-50 diff sustained
  // BUG FIX 2: Increased from (1, 1.5) to (20, 25)
  motionGlobal: 20, // Global frame motion threshold
  motionRoi: 25, // Motion near face region threshold
  
  // Face size change for phone detection (arm extending toward camera)
  // Normal breathing: 0-3% face size change
  // Leaning back/forward: 5-10% change
  // Arm with phone extending: 15-25% change
  // BUG FIX 3: Increased from 0.08 to 0.18 (18% change needed)
  faceSizeChangeThreshold: 0.18,
  
  // Backend upload throttling (ms)
  minSendIntervalMs: 2000, // Increased from 500 to reduce backend spam and false positives
  
  // Phone detection - REMOVED periodic checks
  // BUG FIX 1: Removed phoneDetectionIntervalMs (was firing every 800ms)
  // BUG FIX 1: Removed phoneCheckAfterFaceMs (was 1000ms automatic trigger)
  // Phone is now detected only by COMPOUND signals: multiple indicators together
  phoneConsecutiveFrameThreshold: 3, // Requires 3 consecutive high-confidence frames
});

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function safeNow() {
  return Date.now();
}

function ensureSessionId(existing) {
  if (existing) return existing;
  // Prefer crypto.randomUUID; fallback for older browsers.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `sess_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

function bboxToNormalized(bbox, vw, vh) {
  // MediaPipe Tasks Vision FaceDetector boundingBox is in pixel space of the input frame.
  // We normalize to [0..1] for transport + stable comparisons.
  const x = bbox?.originX ?? 0;
  const y = bbox?.originY ?? 0;
  const w = bbox?.width ?? 0;
  const h = bbox?.height ?? 0;
  const nx = vw ? x / vw : 0;
  const ny = vh ? y / vh : 0;
  const nw = vw ? w / vw : 0;
  const nh = vh ? h / vh : 0;
  const cx = nx + nw / 2;
  const cy = ny + nh / 2;
  return {
    x: clamp(nx, 0, 1),
    y: clamp(ny, 0, 1),
    w: clamp(nw, 0, 1),
    h: clamp(nh, 0, 1),
    cx: clamp(cx, 0, 1),
    cy: clamp(cy, 0, 1),
  };
}

function expandRect(rect, factor) {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const w = rect.w * factor;
  const h = rect.h * factor;
  return {
    x: clamp(cx - w / 2, 0, 1),
    y: clamp(cy - h / 2, 0, 1),
    w: clamp(w, 0, 1),
    h: clamp(h, 0, 1),
  };
}

function computeSmallFrameStats(video, opts) {
  const { size, prevRef, canvasRef } = opts;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) {
    // CRITICAL: Return default values instead of null to prevent motion detection from breaking
    return { meanLuma: 128, meanAbsDiff: 0, size, prev: null, cur: null };
  }

  if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
  const canvas = canvasRef.current;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    // CRITICAL: Return default values instead of null
    return { meanLuma: 128, meanAbsDiff: 0, size, prev: null, cur: null };
  }

  try {
    ctx.drawImage(video, 0, 0, size, size);
    const img = ctx.getImageData(0, 0, size, size);
    const data = img.data;

    let sumLuma = 0;
    let sumAbsDiff = 0;
    const prev = prevRef.current;

    // Luma approximation: 0.299R + 0.587G + 0.114B
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const luma = (0.299 * r + 0.587 * g + 0.114 * b);
      sumLuma += luma;

      if (prev && prev[i] !== undefined) {
        // Mean absolute diff on RGB channels; lightweight motion/freeze heuristic.
        sumAbsDiff += Math.abs(r - prev[i]) + Math.abs(g - prev[i + 1]) + Math.abs(b - prev[i + 2]);
      }
    }

    // Snapshot current frame bytes (used for ROI motion and next tick diff).
    // IMPORTANT: we keep `prev` as the *prior tick* snapshot for motion/occlusion comparisons.
    const cur = new Uint8ClampedArray(data);
    prevRef.current = cur;

    const pixels = size * size;
    const meanLuma = pixels > 0 ? (sumLuma / pixels) : 128; // 0..255, default to 128 if no pixels
    // Normalize diff by pixels and channels (3); typical range 0..255
    // CRITICAL: Ensure we always return a valid number, never NaN or undefined
    const meanAbsDiff = (prev && pixels > 0) ? (sumAbsDiff / (pixels * 3)) : 0;

    return { meanLuma, meanAbsDiff, size, prev, cur };
  } catch (error) {
    console.error('Error computing frame stats:', error);
    // CRITICAL: Return default values on error to prevent monitoring from breaking
    return { meanLuma: 128, meanAbsDiff: 0, size, prev: prevRef.current, cur: null };
  }
}

function computeRegionMotionEnergy(prevSmall, curSmall, size, region) {
  // region is normalized [0..1], computed in the SAME coordinate system as the video frame.
  // We approximate by using the small frame (size x size) since we draw it uniformly.
  if (!prevSmall || !curSmall) return 0;

  const x0 = Math.floor(clamp(region.x, 0, 1) * size);
  const y0 = Math.floor(clamp(region.y, 0, 1) * size);
  const x1 = Math.floor(clamp(region.x + region.w, 0, 1) * size);
  const y1 = Math.floor(clamp(region.y + region.h, 0, 1) * size);

  const ix0 = clamp(x0, 0, size - 1);
  const iy0 = clamp(y0, 0, size - 1);
  const ix1 = clamp(x1, ix0 + 1, size);
  const iy1 = clamp(y1, iy0 + 1, size);

  let sumAbs = 0;
  let count = 0;
  for (let y = iy0; y < iy1; y++) {
    for (let x = ix0; x < ix1; x++) {
      const idx = (y * size + x) * 4;
      sumAbs +=
        Math.abs(curSmall[idx] - prevSmall[idx]) +
        Math.abs(curSmall[idx + 1] - prevSmall[idx + 1]) +
        Math.abs(curSmall[idx + 2] - prevSmall[idx + 2]);
      count += 3;
    }
  }
  return count ? (sumAbs / count) : 0; // 0..255
}

function captureCropsFromVideo(video, rects, maxSidePx = 360) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return [];

  const fullCanvas = document.createElement('canvas');
  fullCanvas.width = vw;
  fullCanvas.height = vh;
  const fullCtx = fullCanvas.getContext('2d', { willReadFrequently: true });
  if (!fullCtx) return [];
  fullCtx.drawImage(video, 0, 0, vw, vh);

  return rects
    .filter(Boolean)
    .slice(0, 2)
    .map((r) => {
      const sx = Math.floor(clamp(r.x, 0, 1) * vw);
      const sy = Math.floor(clamp(r.y, 0, 1) * vh);
      const sw = Math.floor(clamp(r.w, 0, 1) * vw);
      const sh = Math.floor(clamp(r.h, 0, 1) * vh);
      if (sw <= 0 || sh <= 0) return null;

      // Scale down crop for network efficiency while keeping forensic usefulness.
      const scale = Math.min(1, maxSidePx / Math.max(sw, sh));
      const dw = Math.max(1, Math.floor(sw * scale));
      const dh = Math.max(1, Math.floor(sh * scale));

      const cropCanvas = document.createElement('canvas');
      cropCanvas.width = dw;
      cropCanvas.height = dh;
      const cropCtx = cropCanvas.getContext('2d', { willReadFrequently: true });
      if (!cropCtx) return null;

      cropCtx.drawImage(fullCanvas, sx, sy, sw, sh, 0, 0, dw, dh);
      // JPEG is smaller; acceptable because we only need “proof”, not perfect pixel fidelity.
      return cropCanvas.toDataURL('image/jpeg', 0.85);
    })
    .filter(Boolean);
}

/**
 * useDocumentMonitor
 *
 * Params:
 * - enabled: boolean (turn monitoring on/off)
 * - webcamRef: react-webcam ref
 * - udtId, token: viewer identity for backend enforcement
 * - onServerAction: callback({action, reason, verdict, server_score, ...})
 *
 * Returns debug info for UI (optional).
 */
export function useDocumentMonitor({
  enabled,
  webcamRef,
  udtId,
  token,
  monitorSessionId,
  onServerAction,
  onLocalEvent,
  detectionFps = 10,
  gracePeriodEndsAt = null, // Timestamp when grace period ends (null = no grace period)
}) {
  const [monitorState, setMonitorState] = useState(MonitorState.IDLE);
  const [requestsSent, setRequestsSent] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);

  const sessionIdRef = useRef(null);
  const monitorStateRef = useRef(MonitorState.IDLE);
  // BUG FIX 8: Initialize to null, not 0 - prevents epoch timestamp from triggering faceGoneMs
  const lastFaceSeenAtRef = useRef(null);
  const lastSendAtRef = useRef(0);
  const suspiciousCountRef = useRef([]); // timestamps of suspicious events
  const highRiskCountRef = useRef([]); // timestamps of high-risk events
  const consecutiveNoFaceFramesRef = useRef(0); // Track consecutive frames without face (for stability)

  const prevSmallFrameRef = useRef(null);
  const smallCanvasRef = useRef(null);

  const lastFaceRectRef = useRef(null); // normalized rect of last single face
  const lastFaceCenterRef = useRef(null);
  const lastFaceSizeRef = useRef(null); // Track face size for occlusion detection
  const cameraDarkSinceRef = useRef(null);
  const cameraFrozenSinceRef = useRef(null);
  const consecutiveDarkFramesRef = useRef(0); // Track consecutive dark frames for debouncing
  const cameraInitializedAtRef = useRef(null); // Track when camera was first ready
  const lastPhoneCheckRef = useRef(0); // Track last phone detection check
  const phoneSuspicionCountRef = useRef(0); // Track consecutive suspicious frames
  const lastDebugLogRef = useRef(0); // Track last debug log time

  const tickMs = useMemo(() => Math.max(60, Math.floor(1000 / detectionFps)), [detectionFps]);

  useEffect(() => {
    sessionIdRef.current = ensureSessionId(sessionIdRef.current);
  }, []);

  useEffect(() => {
    monitorStateRef.current = monitorState;
  }, [monitorState]);

  useEffect(() => {
    if (!enabled) {
      setMonitorState(MonitorState.IDLE);
      monitorStateRef.current = MonitorState.IDLE;
      return;
    }

    let timer = null;
    let stopped = false;

    const step = async () => {
      if (stopped) return;
      if (document.hidden) return;

      const videoEl = webcamRef?.current?.video;
      if (!videoEl || !videoEl.videoWidth || !videoEl.videoHeight) {
        // Camera not ready yet - this is expected during initialization
        // DEBUG: Log when camera is not ready
        if (process.env.NODE_ENV === 'development') {
          const now = safeNow();
          if (!lastDebugLogRef.current || (now - lastDebugLogRef.current) > 5000) {
            lastDebugLogRef.current = now;
            console.warn('[monitor] ⚠️ Camera not ready:', {
              hasVideoEl: !!videoEl,
              videoWidth: videoEl?.videoWidth || 0,
              videoHeight: videoEl?.videoHeight || 0,
              readyState: videoEl?.readyState || 'N/A'
            });
          }
        }
        return;
      }
      
      // Take a single timestamp for this step as early as possible.
      const now = safeNow();

      // DEBUG: Log when monitoring loop is running (first time only)
      if (process.env.NODE_ENV === 'development' && !monitorStateRef.current._loopStarted) {
        monitorStateRef.current._loopStarted = true;
        console.log('[monitor] ✅ Monitoring loop STARTED - detection active');
      }

      // Track camera initialization time (first frame we process)
      if (!cameraInitializedAtRef.current) {
        cameraInitializedAtRef.current = now;
      }

      // Local pixel-based heuristics (camera covered/dark/frozen)
      const stats = computeSmallFrameStats(videoEl, {
        size: 64,
        prevRef: prevSmallFrameRef,
        canvasRef: smallCanvasRef,
      });
      const meanLuma = stats?.meanLuma ?? 255;
      const meanAbsDiff = stats?.meanAbsDiff ?? 255;
      const prevSmall = stats?.prev || null;
      const curSmall = stats?.cur || null;

      // Camera blocked pattern: sustained very low brightness with debouncing
      // CRITICAL: Only trigger if camera has been initialized for required time (prevents startup false positives)
      const cameraAgeMs = cameraInitializedAtRef.current ? (now - cameraInitializedAtRef.current) : 0;
      const isCameraMature = cameraAgeMs > THRESHOLDS.cameraMaturityMs;
      
      const dark = meanLuma < THRESHOLDS.darkLuma;
      if (dark && isCameraMature) {
        // Increment consecutive dark frames counter
        consecutiveDarkFramesRef.current = (consecutiveDarkFramesRef.current || 0) + 1;
        
        // Only start tracking dark duration if we have enough consecutive dark frames
        if (consecutiveDarkFramesRef.current >= THRESHOLDS.darkConsecutiveFrames) {
          if (!cameraDarkSinceRef.current) cameraDarkSinceRef.current = now;
        }
      } else {
        // Reset counters if not dark or camera not mature
        consecutiveDarkFramesRef.current = 0;
        cameraDarkSinceRef.current = null;
      }

      // Frozen pattern: sustained near-zero frame difference.
      // BUG FIX 10: Only mark frozen if we have a previous frame to compare against
      // This prevents the first frame from being marked as frozen
      const frozen = meanAbsDiff < THRESHOLDS.frozenDiff;
      if (frozen && prevSmall !== null) {
        // Only start frozen timer if we have a PREVIOUS frame to compare against
        if (!cameraFrozenSinceRef.current) cameraFrozenSinceRef.current = now;
      } else {
        cameraFrozenSinceRef.current = null;
      }

      // MediaPipe face detection (objective geometry)
      const detection = await faceDetectionService.detectFaces(videoEl, now);
      const faceCount = detection?.faceCount ?? -1;
      const faces = detection?.faces ?? [];

      // DEBUG: Log MediaPipe detection status (every 2 seconds)
      if (process.env.NODE_ENV === 'development') {
        const logInterval = 2000; // Log every 2 seconds
        if (!lastDebugLogRef.current || (now - lastDebugLogRef.current) > logInterval) {
          lastDebugLogRef.current = now;
          console.log('[monitor] 📊 MediaPipe & Motion Status:', {
            faceCount,
            facesDetected: faces?.length || 0,
            videoReady: videoEl.readyState === 4,
            videoSize: `${videoEl.videoWidth}x${videoEl.videoHeight}`,
            detectionError: detection?.error || null,
            meanAbsDiff: meanAbsDiff.toFixed(2),
          });
        }
      }

      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;

      let normalizedFaces = [];
      if (Array.isArray(faces) && faces.length && faceCount !== -1) {
        normalizedFaces = faces
          .map((f) => bboxToNormalized(f.boundingBox, vw, vh))
          .filter((r) => r.w > 0 && r.h > 0);
      }

      // Determine local event_type - ALWAYS check for camera issues (dark, frozen)
      // These are pixel-based and don't depend on MediaPipe
      let eventType = null;
      let localRisk = 0;

      // CRITICAL: Check for camera blocked/dark REGARDLESS of MediaPipe status
      // This is purely pixel-based analysis and should always work
      if (dark && cameraDarkSinceRef.current && (now - cameraDarkSinceRef.current) > THRESHOLDS.darkHoldMs) {
        // CRITICAL: Only trigger camera blocked if:
        // 1. Camera has been dark for required duration
        // 2. We have enough consecutive dark frames (debouncing)
        // 3. No face was detected recently (within last 5 seconds) - if face was detected, camera is clearly working
        const lastFaceSeen = lastFaceSeenAtRef.current;
        const faceDetectedRecently = lastFaceSeen && (now - lastFaceSeen) < 5000;
        const hasEnoughDarkFrames = consecutiveDarkFramesRef.current >= THRESHOLDS.darkConsecutiveFrames;
        
        if (hasEnoughDarkFrames && !faceDetectedRecently) {
          eventType = 'CAMERA_BLOCKED_LOCAL';
          localRisk = 85;
        }
      } else if (frozen && cameraFrozenSinceRef.current && (now - cameraFrozenSinceRef.current) > THRESHOLDS.frozenHoldMs) {
        eventType = 'CAMERA_FROZEN_LOCAL';
        localRisk = 70;
      }

      // If camera issues detected, report them immediately (don't continue with face-based checks)
      if (eventType) {
        const localEvent = {
          event_type: eventType,
          timestamp_ms: now,
          face_count: faceCount,
          local_risk: localRisk,
        };
        setLastEvent(localEvent);
        onLocalEvent?.(localEvent);

        // CRITICAL: For camera blocked events, send to backend immediately
        if (true) { // Always send camera blocked/frozen events
          const cropRects = [{ x: 0.25, y: 0.18, w: 0.5, h: 0.64 }];
          const croppedImages = captureCropsFromVideo(videoEl, cropRects, 360);
          const payload = {
            session_id: sessionIdRef.current,
            monitor_session_id: sessionIdRef.current,
            udt_id: '',
            token: '',
            cropped_images: croppedImages,
            mediapipe_metadata: {
              face_count: faceCount,
              error: detection?.error || null,
            },
            event_type: eventType,
            timestamp_ms: now,
          };

          // Send to backend
          try {
            await axios.post(`${API}/monitor/face-verdict`, payload, { timeout: 12000 });
            setRequestsSent(prev => prev + 1);
          } catch (err) {
            if (process.env.NODE_ENV === 'development') {
              console.error('[monitor] Failed to send camera blocked event:', err.message);
            }
          }
        }

        // Continue to next monitoring tick
        return;
      }

      // CRITICAL: If faceCount is -1 (MediaPipe failed), skip ALL face-based checks
      // We can only do pixel-based analysis at this point
      if (faceCount === -1) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[monitor] ⚠️ MediaPipe unavailable (faceCount=-1), skipping FACE-based detection (but camera checks already done)');
        }
        
        // Skip all FACE-based checks - we already checked for camera blocked/frozen above
        return;
      }

      // Face disappearance tracking with stability debounce
      // Require multiple consecutive frames without face to prevent false positives
      const wasFaceDetected = lastFaceSeenAtRef.current !== null;
      if (faceCount === 1) {
        // When face is first detected, record it
        if (!wasFaceDetected) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[monitor] Face detected');
          }
        }
        lastFaceSeenAtRef.current = now;
        consecutiveNoFaceFramesRef.current = 0; // Reset counter when face is detected
      } else if (faceCount === 0) {
        // Increment counter when no face is detected
        consecutiveNoFaceFramesRef.current = (consecutiveNoFaceFramesRef.current || 0) + 1;
      } else {
        // Multiple faces - reset counter
        consecutiveNoFaceFramesRef.current = 0;
      }
      
      // Only consider face "gone" if:
      // 1. No face detected for the required time (faceGoneMs)
      // 2. AND we've had at least 3 consecutive frames without face (prevents brief detection failures)
      const faceGoneForMs = lastFaceSeenAtRef.current !== null ? (now - lastFaceSeenAtRef.current) : 0;
      const hasStableNoFace = consecutiveNoFaceFramesRef.current >= 3; // Require 3 consecutive frames

      // Position jump tracking (only when exactly one face is present)
      let posJump = null;
      let faceSizeChange = null;
      if (faceCount === 1 && normalizedFaces[0]) {
        const cur = normalizedFaces[0];
        const prevCenter = lastFaceCenterRef.current;
        const curCenter = { cx: cur.cx, cy: cur.cy };
        if (prevCenter) {
          const dist = Math.hypot(curCenter.cx - prevCenter.cx, curCenter.cy - prevCenter.cy);
          if (dist > THRESHOLDS.faceJumpDist) posJump = { dist };
        }
        lastFaceCenterRef.current = curCenter;
        lastFaceRectRef.current = cur;
        
        // CRITICAL: Detect face size changes (phone coming into frame makes face appear smaller)
        const faceArea = cur.w * cur.h; // Face area (normalized 0-1)
        if (lastFaceSizeRef.current !== null) {
          const sizeChange = Math.abs(faceArea - lastFaceSizeRef.current) / lastFaceSizeRef.current;
          if (sizeChange > THRESHOLDS.faceSizeChangeThreshold) {
            faceSizeChange = { change: sizeChange, current: faceArea, previous: lastFaceSizeRef.current };
          }
        }
        lastFaceSizeRef.current = faceArea;
      } else {
        lastFaceSizeRef.current = null;
      }

      // Motion burst near face region (phone proxy):
      // CRITICAL: Compute motion energy even without face rect - use center region as fallback
      let motionEnergyNearFace = 0;
      if (prevSmall && curSmall) {
        if (lastFaceRectRef.current) {
          // Expanded region around the face, biased downward a bit (where a phone would rise from).
          const r = expandRect(lastFaceRectRef.current, 2.2);
          const biased = { ...r, y: clamp(r.y + r.h * 0.15, 0, 1) };
          motionEnergyNearFace = computeRegionMotionEnergy(prevSmall, curSmall, 64, biased);
        } else {
          // FALLBACK: If no face rect, compute motion in center region (where phone would typically be)
          // This ensures motion detection works even if face detection is temporarily failing
          const centerRegion = { x: 0.3, y: 0.3, w: 0.4, h: 0.4 };
          motionEnergyNearFace = computeRegionMotionEnergy(prevSmall, curSmall, 64, centerRegion);
        }
      }
      // Motion requires BOTH global and ROI to exceed threshold for confidence
      // BUG FIX 2: Changed from (1, 1.5) to (20, 25) - realistic thresholds
      const motionBurst = (meanAbsDiff > THRESHOLDS.motionGlobal) && (motionEnergyNearFace > THRESHOLDS.motionRoi);
      
      // Determine face-based event_type (in addition to camera issues checked above)
      if (faceCount >= THRESHOLDS.lockFaces) {
        // Enforce only when 4+ faces are detected (per policy).
        eventType = 'MULTIPLE_FACES_LOCAL';
        localRisk = 90;
      } else if (faceCount === 0 && faceGoneForMs > THRESHOLDS.faceGoneMs && hasStableNoFace) {
        // Only trigger if face has been gone for required time AND we have stable detection (no false positives)
        eventType = 'FACE_DISAPPEARED';
        localRisk = 60;
      } else if (faceSizeChange && faceCount === 1) {
        // BUG FIX 3: Increased threshold from 0.08 to 0.18
        // Face size change requires 18% change (breathing is 0-3%, normal leaning is 5-10%)
        // Only mark as phone if change is very significant
        eventType = 'FACE_SIZE_CHANGED';
        localRisk = 65; // Medium-high risk - face size change is notable
      } else if (motionBurst && faceCount === 1) {
        // BUG FIX 1: REMOVED periodic phone checks (was every 800ms)
        // BUG FIX 2: FIXED motion thresholds (now 20/25, not 1/1.5)
        // Phone detection now requires COMPOUND signals:
        // - Motion burst BOTH globally and near-face (not just one)
        // - Face must be present
        // - Requires sustained pattern (multiple frames)
        eventType = 'PHONE_CAMERA_SUSPECTED';
        localRisk = 75; // High risk - motion near face is suspicious
      }

      // Compound signal tracking for more reliable phone detection
      // BUG FIX 1: Instead of periodic checks, track CONSECUTIVE suspicious frames
      if (eventType === 'PHONE_CAMERA_SUSPECTED') {
        phoneSuspicionCountRef.current = (phoneSuspicionCountRef.current || 0) + 1;
      } else {
        // Decay suspicion if no phone signal detected
        phoneSuspicionCountRef.current = Math.max(0, (phoneSuspicionCountRef.current || 0) - 0.5);
      }
      
      // Only escalate phone detection to backend after multiple frames show signals
      // This prevents single frame false positives (codec artifacts, MediaPipe noise)
      if (phoneSuspicionCountRef.current >= THRESHOLDS.phoneConsecutiveFrameThreshold && eventType === 'PHONE_CAMERA_SUSPECTED') {
        // Phone detection is confirmed by multiple frames - proceed to backend
        // This is the key fix: we don't fire on every motion, only sustained patterns
        if (process.env.NODE_ENV === 'development') {
          console.log('[monitor] 📸 PHONE suspicion accumulated:', {
            suspicionCount: phoneSuspicionCountRef.current,
            threshold: THRESHOLDS.phoneConsecutiveFrameThreshold,
            motionEnergyNearFace: motionEnergyNearFace.toFixed(2),
            meanAbsDiff: meanAbsDiff.toFixed(2),
            readyForBackend: phoneSuspicionCountRef.current >= THRESHOLDS.phoneConsecutiveFrameThreshold
          });
        }
      } else if (!eventType && motionBurst && faceCount === 1) {
        // Suspicious motion but not yet confirmed as phone
        eventType = 'MOTION_DETECTED';
        localRisk = 40; // Low-moderate risk
      }

      // Update state machine
      const prevState = monitorStateRef.current;
      let nextState = MonitorState.IDLE;
      if (eventType) {
        // Phone camera suspected is always HIGH_RISK regardless of risk score
        if (eventType === 'PHONE_CAMERA_SUSPECTED') {
          nextState = MonitorState.HIGH_RISK;
        } else {
          nextState = localRisk >= 75 ? MonitorState.HIGH_RISK : MonitorState.SUSPICIOUS;
        }
      }
      if (nextState !== prevState) {
        setMonitorState(nextState);
        monitorStateRef.current = nextState;
      }

      if (eventType) {
        const localEvent = {
          event_type: eventType,
          timestamp_ms: now,
          face_count: faceCount,
          local_risk: localRisk,
        };
        setLastEvent(localEvent);
        onLocalEvent?.(localEvent);
      }

      // CRITICAL: For HIGH_RISK events (especially phone camera), send continuously, not just on entry
      // This ensures instant detection and reaction - no waiting for state transitions
      const isHighRiskEvent = nextState === MonitorState.HIGH_RISK;
      const isPhoneSuspected = eventType === 'PHONE_CAMERA_SUSPECTED';
      const entering = (prevState === MonitorState.IDLE) && (nextState !== MonitorState.IDLE);
      
      // CRITICAL: For HIGH_RISK events (phone camera, multiple faces, camera blocked), send immediately
      // Never use periodic checks - BUG FIX 1 removed phoneDetectionIntervalMs entirely
      // Phone detection now happens through compound signal accumulation (phoneSuspicionCountRef)
      const isCriticalEvent = isPhoneSuspected || eventType === 'MULTIPLE_FACES_LOCAL' || eventType === 'CAMERA_BLOCKED_LOCAL';
      const shouldSend = entering || (isHighRiskEvent && isCriticalEvent);
      
      if (!shouldSend) return;

      // CRITICAL: Check grace period before sending to backend
      // During grace period (3.5s after camera ready), suppress violations to allow user positioning
      // Exception: Only suppress face/camera events, not high-confidence violations like screenshots
      const isFaceRelatedEvent = eventType && (
        eventType.includes('FACE') || 
        eventType.includes('CAMERA') || 
        eventType.includes('MULTIPLE') ||
        eventType.includes('BLOCKED') ||
        eventType.includes('FROZEN')
      );
      
      // Suppress face-related violations during grace period (but still log locally)
      // EXCEPTION: Don't suppress phone camera detection - it's critical and should trigger immediately
      // EXCEPTION: Don't suppress multiple faces (4+) - it's critical and should trigger immediately
      // CRITICAL: Always suppress camera blocked during grace period - camera initialization can cause false positives
      const isCameraBlocked = eventType === 'CAMERA_BLOCKED_LOCAL';
      const isMultipleFaces = eventType === 'MULTIPLE_FACES_LOCAL';
      const isCriticalViolation = isPhoneSuspected || isMultipleFaces; // Phone and multiple faces bypass grace period
      const shouldSuppress = gracePeriodEndsAt && now < gracePeriodEndsAt && isFaceRelatedEvent && !isCriticalViolation;
      
      if (shouldSuppress || (isCameraBlocked && gracePeriodEndsAt && now < gracePeriodEndsAt)) {
        const remaining = Math.ceil((gracePeriodEndsAt - now) / 1000);
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[monitor] Suppressed violation during grace period (${remaining}s remaining):`, eventType);
        }
        // Still update local state for debugging, but don't send to backend
        return;
      }

      // For HIGH_RISK violations (4+ faces, camera blocked, phone suspected), send immediately without throttling
      // For SUSPICIOUS violations, apply throttling to avoid spamming
      // Phone camera suspected is HIGH_RISK - send immediately, bypass all throttling
      const isCriticalHighRisk = isHighRiskEvent && (isPhoneSuspected || eventType === 'MULTIPLE_FACES_LOCAL' || eventType === 'CAMERA_BLOCKED_LOCAL');
      
      // Only throttle SUSPICIOUS events, never HIGH_RISK events
      // For phone detection, allow very frequent sends (every 200ms) to catch rapid movements instantly
      if (!isCriticalHighRisk && !isPhoneSuspected) {
        // Throttle sends to avoid spamming during flapping (only for suspicious, not high-risk)
        if (now - lastSendAtRef.current < THRESHOLDS.minSendIntervalMs) return;
      } else if (isPhoneSuspected) {
        // For phone detection, use minimal throttling (200ms) to catch rapid movements
        if (now - lastSendAtRef.current < 200) return;
      }
      lastSendAtRef.current = now;

      // If camera is blocked/dark, face box may not exist. We still send at most one “full-ish” crop:
      // We approximate by capturing a centered crop.
      let cropRects = [];
      const single = normalizedFaces[0] || lastFaceRectRef.current;
      if (single) {
        cropRects = [expandRect(single, 1.6), expandRect(single, 2.2)];
      } else {
        cropRects = [{ x: 0.25, y: 0.18, w: 0.5, h: 0.64 }];
      }

      const croppedImages = captureCropsFromVideo(videoEl, cropRects, 360);
      const payload = {
        session_id: sessionIdRef.current,
        // Backend-authoritative monitoring session id (required by backend).
        monitor_session_id: monitorSessionId,
        udt_id: udtId,
        token,
        cropped_images: croppedImages.slice(0, 2),
        mediapipe_metadata: {
          face_count: faceCount,
          faces: normalizedFaces.slice(0, 5),
          face_gone_for_ms: faceGoneForMs,
          mean_luma: meanLuma,
          mean_abs_diff: meanAbsDiff,
          motion_energy_near_face: motionEnergyNearFace,
          detection_timestamp_ms: now,
        },
        event_type: eventType || 'UNKNOWN',
        timestamp_ms: now,
      };

      try {
        const resp = await axios.post(`${API}/monitor/face-verdict`, payload, { timeout: 12000 });
        setRequestsSent((n) => n + 1);
        onServerAction?.(resp.data);
      } catch (err) {
        // Fail-safe: we do not enforce locally if the backend cannot decide.
        // We intentionally do NOT show toasts/popups from the hook. Viewer should remain silent/stable.
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.debug('[monitor] face-verdict failed', err?.response?.status, err?.response?.data);
        }
      }

      // Update rolling windows for future policy decisions (frontend only; backend is authoritative).
      if (nextState === MonitorState.SUSPICIOUS) {
        suspiciousCountRef.current = [...suspiciousCountRef.current, now].filter((t) => now - t < 5 * 60 * 1000);
      } else if (nextState === MonitorState.HIGH_RISK) {
        highRiskCountRef.current = [...highRiskCountRef.current, now].filter((t) => now - t < 10 * 60 * 1000);
      }
    };

    timer = setInterval(() => {
      // Fire-and-forget; we purposely do not await inside setInterval.
      step();
    }, tickMs);

    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
    // NOTE: monitorState is intentionally NOT a dependency; we want stable timing.
    // We read monitorState via React state; minor staleness only affects "entering",
    // and backend still enforces decisively.
  }, [enabled, webcamRef, udtId, token, monitorSessionId, tickMs, gracePeriodEndsAt]);

  return {
    monitorState,
    requestsSent,
    lastEvent,
    sessionId: sessionIdRef.current,
  };
}

